import { CartAddEvent } from '@theme/events';

const copy = {
  de: { loading: 'Passende Produkte werden geladen …', empty: 'Für dieses Produkt sind gerade keine Empfehlungen verfügbar.', failed: 'Die Empfehlungen konnten nicht geladen werden.', size: 'Größe wählen', soldOut: 'Ausverkauft', choose: 'Auswählen', selected: '✓ Ausgewählt', remove: 'Auswahl entfernen', add: 'Beide in den Warenkorb', adding: 'Wird hinzugefügt …', added: 'Dein Stack ist im Warenkorb.', error: 'Das hat nicht geklappt. Bitte prüfe deinen Warenkorb, bevor du es erneut versuchst.', baseUnavailable: 'Bitte wähle oben eine verfügbare Variante für dein Hauptprodukt.', picked: 'Ausgewählt', removed: 'Auswahl entfernt.', together: 'Zusammen', from: 'ab', savings: 'gespart', preview: 'Vorschau', cartPrice: 'Warenkorb aktuell', baseDiscount: 'Hauptstück −50 %' },
  en: { loading: 'Loading matching pieces …', empty: 'There are no recommendations for this product right now.', failed: 'The recommendations could not be loaded.', size: 'Choose size', soldOut: 'Sold out', choose: 'Select', selected: '✓ Selected', remove: 'Remove selection', add: 'Add both to cart', adding: 'Adding …', added: 'Your stack is in the cart.', error: 'Something went wrong. Please check your cart before trying again.', baseUnavailable: 'Please select an available variant for your main product above.', picked: 'Selected', removed: 'Selection removed.', together: 'Together', from: 'from', savings: 'saved', preview: 'Preview', cartPrice: 'Current cart price', baseDiscount: 'Main piece −50%' },
};

class WSStackBuilder extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.labels = copy[this.dataset.language] || copy.en;
    this.product = JSON.parse(this.querySelector('[data-stack-product]').textContent);
    this.baseVariant = this.product.variants.find(v => v.id === this.product.selected);
    this.pick = null;
    this.products = [];
    this.cards = [];
    this.root = this.dataset.root.replace(/\/?$/, '/');
    this.money = new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: window.Shopify?.currency?.active || this.dataset.currency || 'EUR', maximumFractionDigits: 2 });
    this.addEventListener('click', event => this.onClick(event), { signal: this.controller.signal });
    document.addEventListener('variant:update', event => {
      if (String(event.detail?.data?.productId) !== this.dataset.productId) return;
      this.baseVariant = this.product.variants.find(v => v.id === Number(event.detail.resource?.id));
      if (this.baseVariant) this.baseVariant.available = event.detail.resource.available;
      this.refresh();
    }, { signal: this.controller.signal });
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape' && !this.el('panel').hidden) {
        this.toggle(false);
        this.el('toggle').focus();
        event.stopPropagation();
      }
    }, { signal: this.controller.signal });
    this.refresh();
  }

  disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  el(name) { return this.querySelector(`[data-stack-${name}]`); }
  node(tag, className, text) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text !== undefined) el.textContent = text;
    return el;
  }
  price(cents) { return this.money.format(cents / 100); }
  pair(variant) {
    if (!this.baseVariant || !variant) return null;
    const base = this.baseVariant.price;
    const extra = variant.price;
    const saving = Math.round(Math.min(base, extra) * .5);
    return { original: base + extra, total: base + extra - saving, saving, extra: extra <= base ? extra - saving : extra, baseDiscount: extra > base };
  }
  image(src, alt) {
    const el = this.node('img', 'ws-stack__product-image');
    if (src) { const url = new URL(src, location.origin); url.searchParams.set('width', '360'); el.src = url.href; }
    el.alt = alt; el.width = 360; el.height = 360; el.loading = 'lazy';
    return el;
  }
  status(text, error = false) { this.el('status').textContent = text; this.el('status').dataset.error = String(error); }
  onClick(event) {
    const button = event.target.closest('button');
    if (!button || !this.contains(button) || this.busy) return;
    if (button.hasAttribute('data-stack-toggle')) this.toggle(this.el('panel').hidden);
    if (button.hasAttribute('data-stack-retry')) this.loadProducts();
    if (button.hasAttribute('data-stack-scroll')) {
      const grid = this.el('products');
      grid.scrollBy({ left: Number(button.dataset.stackScroll) * (grid.firstElementChild?.getBoundingClientRect().width + 10 || grid.clientWidth), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
    if (button.hasAttribute('data-stack-add')) this.addToCart();
  }
  toggle(open) {
    this.el('panel').hidden = !open;
    this.el('toggle').setAttribute('aria-expanded', String(open));
    this.querySelector('.ws-stack__start').hidden = open;
    this.querySelector('.ws-stack__close').hidden = !open;
    if (open) this.loadProducts();
  }

  refresh() {
    this.cards.forEach(card => {
      const { product, select, choose, price, pairPrice, element } = card;
      const selected = this.pick?.product.id === product.id;
      const variant = card.variant;
      const fallback = product.variants.filter(v => v.available).sort((a,b) => a.price - b.price)[0];
      const shown = variant || fallback;
      const offer = this.pair(shown);
      element.dataset.selected = String(selected);
      choose.textContent = selected ? '✓' : fallback ? '+' : '−';
      choose.setAttribute('aria-pressed', String(selected));
      const chooseLabel = `${selected ? this.labels.remove : !fallback ? this.labels.soldOut : !variant ? this.labels.size : this.labels.choose}: ${product.title}`;
      choose.setAttribute('aria-label', chooseLabel);
      choose.title = chooseLabel;
      choose.disabled = this.busy || (!selected && !variant?.available);
      if (select) select.disabled = this.busy;
      price.replaceChildren();
      if (shown) {
        price.append(this.node('span', '', `${!variant && product.price_varies ? `${this.labels.from} ` : ''}${this.price(offer?.extra ?? shown.price)}`));
        if (offer && offer.extra < shown.price) price.append(this.node('s', 'ws-stack__original', this.price(shown.price)));
      }
      pairPrice.textContent = offer ? `${offer.baseDiscount ? `${this.labels.baseDiscount} · ` : ''}${this.labels.together} ${!variant && product.price_varies ? `${this.labels.from} ` : ''}${this.price(offer.total)}` : '';
    });
    this.el('checkout').hidden = !this.pick;
    const summary = this.el('summary');
    summary.replaceChildren();
    const offer = this.pick && this.pair(this.pick.variant);
    if (offer) {
      summary.append(this.node('strong', '', this.price(offer.total)), this.node('s', 'ws-stack__original', this.price(offer.original)), this.node('span', 'ws-stack__savings', `${this.price(offer.saving)} ${this.labels.savings}`));
      if (this.dataset.offerPreview !== 'false') summary.append(this.node('span', 'ws-stack__savings', `${this.labels.preview} · ${this.labels.cartPrice} ${this.price(offer.original)}`));
    }
    this.el('add').textContent = this.busy ? this.labels.adding : this.labels.add;
    this.el('add').disabled = this.busy || !this.pick?.variant.available || !this.baseVariant?.available;
    if (this.pick && !this.baseVariant?.available) this.status(this.labels.baseUnavailable, true);
    else if (this.el('status').textContent === this.labels.baseUnavailable) this.status('');
  }

  async get(url, type = 'json') {
    const response = await fetch(url, { signal: AbortSignal.timeout(12000) });
    if (!response.ok) throw new Error('Product request failed');
    return response[type]();
  }
  async loadProducts() {
    if (this.loaded || this.loading) return;
    this.loading = true;
    this.el('loading').textContent = this.labels.loading;
    this.el('retry').hidden = true;
    try {
      // Reuse the exact section, intent, limit and curated order from this PDP.
      const source = [...document.querySelectorAll('product-recommendations')].find(el => el.dataset.productId === this.dataset.productId);
      if (!source) { this.el('loading').textContent = this.labels.empty; return; }
      const url = new URL(source.dataset.url, location.origin);
      url.searchParams.set('section_id', source.dataset.sectionId);
      url.searchParams.set('product_id', this.dataset.productId);
      url.searchParams.set('intent', source.dataset.intent || 'related');
      const html = await this.get(url, 'text');
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const paths = [...new Set([...doc.querySelectorAll('product-recommendations a[href*="/products/"]')].map(a => new URL(a.getAttribute('href'), location.origin).pathname))].slice(0, 10);
      const results = await Promise.allSettled(paths.map(path => this.get(`${path}.js`)));
      this.products = results.filter(r => r.status === 'fulfilled').map(r => r.value).filter(p => String(p.id) !== this.dataset.productId && !p.requires_selling_plan);
      if (paths.length && !this.products.length && results.some(r => r.status === 'rejected')) throw new Error('Products unavailable');
      this.loaded = true;
      this.el('loading').textContent = this.products.length ? '' : this.labels.empty;
      this.renderProducts();
    } catch {
      this.el('loading').textContent = this.labels.failed;
      this.el('retry').hidden = false;
    } finally { this.loading = false; }
  }

  renderProducts() {
    const grid = this.el('products');
    grid.replaceChildren();
    this.cards = this.products.map(product => {
      const element = this.node('div', 'ws-stack__product');
      element.setAttribute('role', 'listitem');
      const link = this.node('a', 'ws-stack__product-link');
      link.href = `${this.root}products/${product.handle}`;
      link.append(this.image(product.featured_image, product.title), this.node('p', 'ws-stack__product-name', product.title));
      const price = this.node('p', 'ws-stack__product-price');
      const pairPrice = this.node('p', 'ws-stack__pair-price');
      const options = this.node('div', 'ws-stack__product-options');
      const available = product.variants.filter(v => v.available);
      const choose = this.node('button', 'ws-stack__choose');
      choose.type = 'button';
      const card = { product, element, choose, price, pairPrice, variant: product.variants.length === 1 ? available[0] : null };
      if (product.variants.length > 1 && available.length) {
        const select = this.node('select', 'ws-stack__select');
        select.setAttribute('aria-label', `${product.title}: ${this.labels.size}`);
        select.add(new Option(this.labels.size, ''));
        product.variants.forEach(v => {
          const option = new Option(`${v.title}${v.available ? '' : ` — ${this.labels.soldOut}`}`, String(v.id));
          option.disabled = !v.available;
          select.add(option);
        });
        select.addEventListener('change', () => {
          card.variant = product.variants.find(v => String(v.id) === select.value);
          if (this.pick?.product.id === product.id) this.pick = card.variant?.available ? { product, variant: card.variant } : null;
          this.refresh();
        });
        card.select = select;
        options.append(select);
      }
      choose.addEventListener('click', () => {
        if (this.busy || !card.variant?.available) return;
        this.pick = this.pick?.product.id === product.id ? null : { product, variant: card.variant };
        this.refresh();
        this.status(this.pick ? `${product.title}: ${this.labels.picked}.` : this.labels.removed);
      });
      options.append(choose);
      element.append(link, price, pairPrice, options);
      grid.append(element);
      return card;
    });
    this.el('arrows').hidden = this.products.length < 3;
    this.refresh();
  }

  async addToCart() {
    if (this.busy || !this.pick?.variant.available) return;
    const form = document.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
    const liveId = Number(form?.querySelector('input[name="id"]')?.value);
    if (liveId) this.baseVariant = this.product.variants.find(v => v.id === liveId);
    if (!this.baseVariant?.available) { this.refresh(); return; }
    this.busy = true;
    this.refresh();
    this.status('');
    const items = [{ id: this.baseVariant.id, quantity: 1 }, { id: this.pick.variant.id, quantity: 1 }];
    const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map(el => el.dataset.sectionId).filter(Boolean))].slice(0, 5);
    try {
      const response = await fetch(`${this.root}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: location.pathname }), signal: AbortSignal.timeout(20000),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || this.labels.error);
      let cart;
      try { cart = await this.get(`${this.root}cart.js`); } catch { /* section HTML still updates the drawer */ }
      document.dispatchEvent(new CartAddEvent(cart, this.dataset.productId, { source: 'product-form-component', productId: this.dataset.productId, itemCount: items.length, sections: result.sections }));
      this.pick = null;
      this.status(this.labels.added);
    } catch (error) {
      this.status(error.name === 'Error' ? error.message : this.labels.error, true);
    } finally { this.busy = false; this.refresh(); }
  }
}

if (!customElements.get('ws-stack-builder')) customElements.define('ws-stack-builder', WSStackBuilder);
